import React, { useState, useCallback, useEffect } from 'react'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useQuery, useLazyQuery } from '@apollo/client'
import { Row, Col, Table, Form, List, notification } from 'antd'
import {
  EyeOutlined,
  DownloadOutlined,
  ExceptionOutlined,
} from '@ant-design/icons'
import { map, size, isEmpty, values, includes, join, findKey } from 'lodash-es'
import { Button, Panel } from '../../../core/components/styled'
import { formatDate } from '../../../core/utils/services/formatter'
import {
  timeBetween,
  formatSecondsToTime,
} from '../../../core/utils/services/date-utils'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { getAutoEatMenuUrl, getAutoEatDishReviewTaskUrl } from '../../helpers'
import {
  PageActions,
  PageHeader,
  PageTitle,
} from '../../../common/components/styled'
import { Spinner } from '../../../common/components'
import {
  useDocumentTitle,
  useDocumentVisibility,
} from '../../../common/hooks/effects'
import {
  SCRAPING_SESSION,
  SCRAPING_SESSION_VALIDATION_STATES,
  SCRAPING_TEST_CASE_RESULTS,
  SCRAPING_RESPONSE_REPORT_URL,
} from '../../graphql/queries'
import { generateMenuMetricsReport } from '../../rest'
import {
  ScrapingSessionStatus,
  ScrapingSessionValidation,
  ScrapingSessionTagScores,
  ScrapingSessionValidationStatus,
  ScrapingMenuValidationModal,
  ScrapingMenuValidationStatus,
  ScrapingResponseStatus,
  JsonViewModal,
  ScrapingSessionLogsModal,
  renderValidationMetric,
} from './sections'
import {
  SCRAPING_RESPONSE_STATUS_IN_PROGRESS,
  SCRAPING_RESPONSE_STATUS_ERROR,
  SCRAPING_RESPONSE_STATUSES,
  SCRAPING_RESPONSE_TYPE_ERROR,
  SCRAPING_FLAGS,
  SCRAPING_SESSION_VALIDATION_COMPLETED,
  SCRAPING_SESSION_VALIDATION_IN_PROGRESS,
  SCRAPING_SESSION_VALIDATION_POLL_INTERVAL_MS,
  MENU_VALIDATION_STATUSES,
} from '../../constants'
import { formatMetricPercent } from '../../utils'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '../../../common/constants'
import fileDownload from 'js-file-download'

const generateMenuValidationMetrics = async (
  sessionId,
  setIsGeneratingMenuValidationMetrics,
) => {
  setIsGeneratingMenuValidationMetrics(true)
  try {
    const reportResponse = await generateMenuMetricsReport(sessionId)
    fileDownload(
      reportResponse.data,
      `MenuValidationMetrics_SessionId-${sessionId}.csv`,
    )
  } catch (e) {
    notification.open({
      message: 'Failed to generate menu validation metrics.',
      description: e.message,
      type: 'error',
      duration: 0,
    })
  } finally {
    setIsGeneratingMenuValidationMetrics(false)
  }
}

const TABLE_SORT_ORDERS = { asc: 'ascend', desc: 'descend' }

const ScrapingSessionPage = () => {
  useDocumentTitle('Scraping session')
  const history = useHistory()
  const { sessionId } = useParams()
  const { pathname, search } = useLocation()
  const { page = DEFAULT_PAGE, pageSize = 10, scoreSort } = getQueryParams(
    search,
  )
  const validationScoreSortOrder = TABLE_SORT_ORDERS[scoreSort]
    ? scoreSort
    : null

  const [jsonViewModalSettings, setJsonViewModalSettings] = useState({
    isModalOpen: false,
    title: '',
    data: {},
  })

  const [
    menuScrapingSessionLogsModalSettings,
    setMenuScrapingSessionLogsModalSettings,
  ] = useState({
    title: null,
    isModalOpen: false,
    menuId: null,
    sessionId: null,
  })

  const [
    isGeneratingMenuValidationMetrics,
    setIsGeneratingMenuValidationMetrics,
  ] = useState(false)

  const [scrapingReportResponseId, setScrapingReportResponseId] = useState()
  const [
    menuValidationModalSettings,
    setMenuValidationModalSettings,
  ] = useState({ isModalOpen: false, scrapingTestCaseResult: undefined })

  const isPageVisible = useDocumentVisibility()

  // No network status notifications, so refetches keep the page mounted.
  const {
    data: { scrapingSession } = {},
    loading: isLoadingSession,
    refetch: refetchScrapingSession,
  } = useQuery(SCRAPING_SESSION, {
    variables: { id: sessionId },
    fetchPolicy: 'no-cache',
  })
  const isValidationRunning =
    scrapingSession?.validationStatus ===
    SCRAPING_SESSION_VALIDATION_IN_PROGRESS

  const {
    data: { scrapingTestCaseResults = {} } = {},
    loading: isLoadingTestCaseResults,
    refetch: refetchTestCaseResults,
  } = useQuery(SCRAPING_TEST_CASE_RESULTS, {
    variables: {
      scrapingSessionId: sessionId,
      page,
      pageSize,
      validationScoreOrder: validationScoreSortOrder,
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  })

  // Poll only the validation status while it runs, then reload once.
  const {
    data: { scrapingSessionValidationStates: validationStates } = {},
  } = useQuery(SCRAPING_SESSION_VALIDATION_STATES, {
    variables: { ids: [sessionId] },
    fetchPolicy: 'no-cache',
    skip: !isValidationRunning || !isPageVisible,
    pollInterval: SCRAPING_SESSION_VALIDATION_POLL_INTERVAL_MS,
  })

  useEffect(() => {
    const [validationState] = validationStates || []
    if (
      !validationState ||
      validationState.validationStatus ===
        SCRAPING_SESSION_VALIDATION_IN_PROGRESS
    ) {
      return
    }

    refetchScrapingSession()
    refetchTestCaseResults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationStates])

  const [
    getScrapingResponseReportUrl,
    {
      data: { scrapingResponseReportUrl } = {},
      loading: isGeneratingScrapingReportUrl,
    },
  ] = useLazyQuery(SCRAPING_RESPONSE_REPORT_URL, {
    onCompleted() {
      window.open(scrapingResponseReportUrl, '_blank')
    },
    onError({ message }) {
      notification.open({
        message: 'Download scraping report failed.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
    fetchPolicy: 'no-cache',
  })

  const handleDownloadScrapingReport = useCallback(
    scrapingResponse => () => {
      setScrapingReportResponseId(scrapingResponse.id)

      getScrapingResponseReportUrl({
        variables: {
          scrapingResponseId: scrapingResponse.id,
        },
      })
    },
    [getScrapingResponseReportUrl],
  )

  const tableColumns = [
    {
      title: 'Menu ID',
      dataIndex: ['scrapingTestCase', 'menuId'],
      width: '45px',
      ellipsis: true,
      render: menuId => (
        <a
          href={`${getAutoEatMenuUrl(menuId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {menuId}
        </a>
      ),
    },
    {
      title: 'DR Task URL',
      dataIndex: ['scrapingTestCase', 'menuId'],
      width: '60px',
      ellipsis: true,
      render: menuId => (
        <a
          href={`${getAutoEatDishReviewTaskUrl(menuId)}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Link
        </a>
      ),
    },
    {
      title: 'Menu URL',
      dataIndex: ['scrapingTestCase', 'menuUrl'],
      width: '140px',
      ellipsis: true,
      render: menuUrl => (
        <a href={menuUrl} target="_blank" rel="noopener noreferrer">
          {menuUrl}
        </a>
      ),
    },
    {
      title: 'Scraping status',
      dataIndex: 'scrapingResponse',
      width: '100px',
      filters: map(values(SCRAPING_RESPONSE_STATUSES), ({ name, value }) => ({
        text: name,
        value,
      })),
      onFilter: (outcomeType, scrapingTestCaseResult) => {
        if (outcomeType === SCRAPING_RESPONSE_STATUS_ERROR) {
          return (
            scrapingTestCaseResult?.scrapingResponse?.type ===
            SCRAPING_RESPONSE_TYPE_ERROR
          )
        }

        return (
          scrapingTestCaseResult?.scrapingResponse?.response?.status ===
          outcomeType
        )
      },
      render: scrapingResponse => {
        if (!scrapingResponse) {
          return (
            <ScrapingResponseStatus
              status={SCRAPING_RESPONSE_STATUS_IN_PROGRESS}
            />
          )
        }

        if (scrapingResponse.type === SCRAPING_RESPONSE_TYPE_ERROR) {
          return (
            <ScrapingResponseStatus
              status={SCRAPING_RESPONSE_STATUS_ERROR}
              code={scrapingResponse.response?.errorCode}
            />
          )
        }

        return (
          <ScrapingResponseStatus status={scrapingResponse.response.status} />
        )
      },
    },
    {
      title: 'Validation status',
      dataIndex: 'validationStatus',
      width: '140px',
      filters: map(values(MENU_VALIDATION_STATUSES), ({ name, value }) => ({
        text: name,
        value,
      })),
      onFilter: (status, scrapingTestCaseResult) =>
        scrapingTestCaseResult.validationStatus === status,
      render: status => <ScrapingMenuValidationStatus status={status} />,
    },
    {
      title: 'Validation score',
      width: '90px',
      key: 'validationScore',
      sorter: true,
      sortOrder: TABLE_SORT_ORDERS[validationScoreSortOrder],
      render: testCaseResult =>
        renderValidationMetric(
          formatMetricPercent(
            testCaseResult.validationMetrics?.menu?.validationScore,
          ),
        ),
    },
    {
      title: 'Duration',
      dataIndex: ['scrapingResponse', 'response', 'duration'],
      width: '100px',
      render: durationInSeconds =>
        durationInSeconds && formatSecondsToTime(durationInSeconds),
    },
    {
      title: 'Menu annotation',
      width: '150px',
      dataIndex: ['scrapingTestCase', 'scrapedData', 'menuAnnotations'],
      render: menuAnnotations =>
        map(menuAnnotations || [], ({ fullMenu }) => fullMenu).join(', '),
    },
    {
      title: 'Flags',
      width: '175px',
      dataIndex: ['scrapingResponse', 'response', 'flags'],
      filters: map(values(SCRAPING_FLAGS), ({ name, value }) => ({
        text: name,
        value,
      })),
      onFilter: (flag, scrapingTestCaseResult) =>
        includes(
          map(
            scrapingTestCaseResult?.scrapingResponse?.response?.flags,
            'text',
          ),
          flag,
        ),
      render: flags => join(map(flags, 'text'), ', '),
    },
    {
      title: 'Scraping instr.',
      width: '100px',
      dataIndex: ['scrapingTestCase', 'scrapingInstructions'],
      align: 'center',
      render: scrapingInstructions => (
        <Button
          icon={<EyeOutlined />}
          onClick={() =>
            setJsonViewModalSettings({
              isModalOpen: true,
              title: 'Scraping instructions',
              data: scrapingInstructions,
            })
          }
        />
      ),
    },
    {
      title: 'Expected data',
      width: '100px',
      dataIndex: ['scrapingTestCase', 'scrapedData'],
      align: 'center',
      render: expectedData => (
        <Button
          icon={<EyeOutlined />}
          onClick={() =>
            setJsonViewModalSettings({
              isModalOpen: true,
              title: 'Expected data',
              data: expectedData,
            })
          }
        />
      ),
    },
    {
      title: 'Scraped data',
      width: '100px',
      dataIndex: ['scrapingResponse', 'response'],
      align: 'center',
      render: scrapedData => (
        <Button
          icon={<EyeOutlined />}
          disabled={!scrapedData}
          onClick={() =>
            setJsonViewModalSettings({
              isModalOpen: true,
              title: 'Scraped data',
              data: scrapedData,
            })
          }
        />
      ),
    },
    {
      title: 'Validation details',
      width: '100px',
      align: 'center',
      render: testCaseResult => (
        <Button
          icon={<EyeOutlined />}
          disabled={isEmpty(testCaseResult.validationMetrics)}
          onClick={() =>
            setMenuValidationModalSettings({
              isModalOpen: true,
              scrapingTestCaseResult: testCaseResult,
            })
          }
        />
      ),
    },
    {
      title: 'New dishes',
      width: '80px',
      dataIndex: 'newDishes',
      render: (newDishes, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(newDishes)}
            onClick={() =>
              fileDownload(
                JSON.stringify(newDishes, null, 2),
                `${testCaseResult.id} - New_dishes.json`,
              )
            }
          >
            {size(newDishes)}
          </Button>
        ),
    },
    {
      title: 'Missing dishes',
      width: '85px',
      dataIndex: 'missingDishes',
      render: (missingDishes, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(missingDishes)}
            onClick={() =>
              fileDownload(
                JSON.stringify(missingDishes, null, 2),
                `${testCaseResult.id} - Missing_dishes.json`,
              )
            }
          >
            {size(missingDishes)}
          </Button>
        ),
    },
    {
      title: 'New menu titles',
      width: '85px',
      dataIndex: 'newMenuTitles',
      render: (newMenuTitles, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(newMenuTitles)}
            onClick={() =>
              fileDownload(
                JSON.stringify(newMenuTitles, null, 2),
                `${testCaseResult.id} - New_menu_titles.json`,
              )
            }
          >
            {size(newMenuTitles)}
          </Button>
        ),
    },
    {
      title: 'Missing menu titles',
      width: '85px',
      dataIndex: 'missingMenuTitles',
      render: (missingMenuTitles, testCaseResult) =>
        testCaseResult.scrapingResponse && (
          <Button
            type="link"
            disabled={isEmpty(missingMenuTitles)}
            onClick={() =>
              fileDownload(
                JSON.stringify(missingMenuTitles, null, 2),
                `${testCaseResult.id} - Missing_menu_titles.json`,
              )
            }
          >
            {size(missingMenuTitles)}
          </Button>
        ),
    },
    {
      title: 'Logs',
      width: '80px',
      align: 'center',
      dataIndex: ['scrapingResponse', 'response', 'correlationEvent'],
      render: correlationEvent => (
        <Button
          icon={<ExceptionOutlined />}
          disabled={!correlationEvent}
          onClick={() =>
            setMenuScrapingSessionLogsModalSettings({
              title: 'Menu scraping logs',
              menuId: correlationEvent.menuId,
              sessionId: correlationEvent.sessionId,
              isModalOpen: true,
            })
          }
        />
      ),
    },
    {
      title: 'Scraping exceptions',
      width: '110px',
      dataIndex: ['scrapingResponse', 'response', 'exceptions'],
      align: 'center',
      render: (exceptions, testCaseResult) => (
        <Button
          disabled={isEmpty(exceptions)}
          icon={<DownloadOutlined />}
          onClick={() =>
            fileDownload(
              JSON.stringify(exceptions, null, 2),
              `${testCaseResult.id} - Exceptions.json`,
            )
          }
        />
      ),
    },
    {
      title: 'Scraping Report',
      width: '110px',
      dataIndex: ['scrapingResponse'],
      align: 'center',
      render: scrapingResponse => (
        <Button
          disabled={!scrapingResponse?.response?.scrapingReport?.fileKey}
          icon={<DownloadOutlined />}
          loading={
            isGeneratingScrapingReportUrl &&
            scrapingResponse.id === scrapingReportResponseId
          }
          onClick={handleDownloadScrapingReport(scrapingResponse)}
        />
      ),
    },
  ]

  return isLoadingSession ? (
    <Spinner />
  ) : (
    <>
      <PageHeader>
        <PageTitle>Scraping session details</PageTitle>
        <PageActions>
          <Button
            type="primary"
            margin="no small no no"
            onClick={() =>
              generateMenuValidationMetrics(
                sessionId,
                setIsGeneratingMenuValidationMetrics,
              )
            }
            loading={isGeneratingMenuValidationMetrics}
          >
            Generate menu validation metrics
          </Button>
          <Button
            margin="no small no no"
            onClick={() => history.push('/scraping/sessions/history')}
          >
            Go back
          </Button>
        </PageActions>
      </PageHeader>
      <Panel margin="small no">
        <Form layout="vertical">
          <Row gutter={36}>
            <Col>
              <Form.Item label="ID">{scrapingSession.id}</Form.Item>
            </Col>
            <Col>
              <Form.Item label="Status">
                <ScrapingSessionStatus status={scrapingSession.status} />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Validation status">
                <ScrapingSessionValidationStatus
                  status={scrapingSession.validationStatus}
                  scrapingStatus={scrapingSession.status}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Started by">
                {scrapingSession.createdBy?.name || 'System'}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Started at">
                {formatDate(scrapingSession.startedAt)}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Completed at">
                {formatDate(scrapingSession.completedAt)}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Validated at">
                {formatDate(scrapingSession.validatedAt)}
              </Form.Item>
            </Col>
            <Col>
              <Form.Item label="Duration">
                {scrapingSession.completedAt &&
                  timeBetween(
                    scrapingSession.startedAt,
                    scrapingSession.completedAt,
                  )}
              </Form.Item>
            </Col>
          </Row>
          <ScrapingSessionValidation
            validationResult={scrapingSession.validationResult}
          />
          <ScrapingSessionTagScores
            sessionId={sessionId}
            isValidated={
              scrapingSession.validationStatus ===
              SCRAPING_SESSION_VALIDATION_COMPLETED
            }
          />
        </Form>
      </Panel>
      <Table
        loading={isLoadingTestCaseResults}
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingTestCaseResults.scrapingTestCaseResults}
        scroll={{ x: 3000, scrollToFirstRowOnChange: false }}
        size="small"
        onChange={(pagination, filters, sorter, { action }) => {
          if (action === 'filter') return

          const scoreSort =
            findKey(TABLE_SORT_ORDERS, order => order === sorter.order) || null
          const nextUrl = createUrl(pathname, search, {
            page:
              action === 'sort' && scoreSort !== validationScoreSortOrder
                ? DEFAULT_PAGE
                : pagination.current,
            scoreSort,
          })

          if (nextUrl !== `${pathname}${search}`) {
            history.push(nextUrl, { preserveScroll: true })
          }
        }}
        expandable={{
          columnWidth: '40px',
          expandedRowRender: testCaseResult => (
            <List
              style={{
                height: 400,
                overflow: 'auto',
                padding: '0 16px',
              }}
              dataSource={testCaseResult.scrapingResponse.response.messages}
              renderItem={message => <List.Item>{message.text}</List.Item>}
            />
          ),
          rowExpandable: testCaseResult =>
            !isEmpty(testCaseResult.scrapingResponse?.response.messages),
        }}
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          showSizeChanger: false,
          total: scrapingTestCaseResults.total,
        }}
      />

      <ScrapingSessionLogsModal
        {...menuScrapingSessionLogsModalSettings}
        closeModal={() =>
          setMenuScrapingSessionLogsModalSettings({
            title: null,
            menuId: null,
            scrapingSessionId: null,
            isModalOpen: null,
          })
        }
      />

      <ScrapingMenuValidationModal
        {...menuValidationModalSettings}
        closeModal={() =>
          setMenuValidationModalSettings({
            isModalOpen: false,
            scrapingTestCaseResult: undefined,
          })
        }
      />

      <JsonViewModal
        {...jsonViewModalSettings}
        closeModal={() =>
          setJsonViewModalSettings({
            isModalOpen: false,
            data: {},
            title: '',
          })
        }
      />
    </>
  )
}

export default ScrapingSessionPage
